/** Reads intrinsic width/height (and duration, for video) from a File by loading it into a
 * throwaway <img>/<video> element — used anywhere a file needs its dimensions captured at upload
 * time before it's ever added to a project's own MediaAsset store (banners, presets' loop
 * uploads). Resolves to an empty object rather than rejecting on error, matching every call
 * site's own "best effort, missing dimensions just means no aspect-ratio hint later" tolerance. */
export function getImageDimensions({
	file,
}: {
	file: File;
}): Promise<{ width?: number; height?: number; duration?: number }> {
	return new Promise((resolve) => {
		const image = new Image();
		const objectUrl = URL.createObjectURL(file);

		const cleanup = () => {
			URL.revokeObjectURL(objectUrl);
			image.remove();
		};

		image.addEventListener("load", () => {
			resolve({ width: image.naturalWidth, height: image.naturalHeight });
			cleanup();
		});
		image.addEventListener("error", () => {
			resolve({});
			cleanup();
		});

		image.src = objectUrl;
	});
}

export function getVideoDimensionsAndDuration({
	file,
}: {
	file: File;
}): Promise<{ width?: number; height?: number; duration?: number }> {
	return new Promise((resolve) => {
		const video = document.createElement("video");
		const objectUrl = URL.createObjectURL(file);

		const cleanup = () => {
			URL.revokeObjectURL(objectUrl);
			video.remove();
		};

		video.addEventListener("loadedmetadata", () => {
			resolve({
				width: video.videoWidth,
				height: video.videoHeight,
				duration: Number.isFinite(video.duration) ? video.duration : undefined,
			});
			cleanup();
		});
		video.addEventListener("error", () => {
			resolve({});
			cleanup();
		});

		video.src = objectUrl;
		video.load();
	});
}
